import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Chatbot from '../components/Chatbot';
import Footer from '../components/Footer';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={true} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        <article className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 mt-0">Terms of Service</h1>
          
          <div className="text-sm text-gray-600 mb-8">
            Last Updated: January 13, 2025
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using GetFlowDocs ("FlowDocs", "we", "our", or "us"), you agree to be bound by these Terms of Service ("Terms"). 
              If you disagree with any part of these terms, you do not have permission to access the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-3">FlowDocs is a platform that provides automated documentation, analysis, and management tools for Salesforce Flows and other automation platforms. Our service includes:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>AI-powered flow summarization and documentation</li>
              <li>Performance analysis and scoring</li>
              <li>Field impact analysis</li>
              <li>Team collaboration features</li>
              <li>Integration with Salesforce and other platforms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Account Registration and Security</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Account Creation</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>You must provide accurate, complete, and current information</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You must be at least 18 years old to use this service</li>
              <li>One person or legal entity may not maintain more than one account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Account Security</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You are responsible for all activities that occur under your account</li>
              <li>You must notify us immediately of any unauthorized use</li>
              <li>We reserve the right to suspend accounts that we reasonably believe may have been compromised</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
            <p className="text-gray-700 mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use the service for any illegal purpose or in violation of any laws</li>
              <li>Attempt to gain unauthorized access to any portion of the service</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Reverse engineer or attempt to extract the source code</li>
              <li>Use the service to transmit malware or malicious code</li>
              <li>Resell, redistribute, or create derivative works of the service</li>
              <li>Use automated systems to access the service without our permission</li>
              <li>Violate the intellectual property rights of others</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Salesforce Integration</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Authorization</h3>
            <p className="text-gray-700 mb-3">By connecting your Salesforce organization, you:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Authorize FlowDocs to access your Salesforce metadata</li>
              <li>Confirm you have the authority to grant such access</li>
              <li>Understand that we will store and process your flow metadata</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Data Access</h3>
            <p className="text-gray-700 mb-3">FlowDocs accesses only the minimum data required to provide our services:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Flow definitions and metadata</li>
              <li>Object and field metadata</li>
              <li>Flow execution history (when authorized)</li>
              <li>User information necessary for authentication</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Your Content</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>You retain ownership of all content you submit to FlowDocs</li>
              <li>You grant us a license to use, store, and process your content solely to provide our services</li>
              <li>You represent that you have the right to grant this license</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Our Service</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>FlowDocs and its original content, features, and functionality are owned by us</li>
              <li>Our service is protected by copyright, trademark, and other laws</li>
              <li>Our trademarks may not be used without our express written permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. AI-Generated Content</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Nature of AI Content</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Flow summaries and analysis are generated using artificial intelligence</li>
              <li>AI-generated content is provided "as is" without warranties</li>
              <li>You should review and verify all AI-generated content before use</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">7.2 Usage Rights</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You may use AI-generated summaries for your internal business purposes</li>
              <li>You are responsible for any use of AI-generated content</li>
              <li>We do not claim ownership of AI-generated content specific to your flows</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Payment Terms</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 Subscription Fees</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Subscription fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>We reserve the right to change fees upon 30 days' notice</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">8.2 Free Services</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We may offer certain services for free, including trials or limited-use tiers</li>
              <li>Free services are provided "as is" without any warranty</li>
              <li>We may discontinue free services at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Service Availability and Modifications</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 Availability</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>We strive for high availability but do not guarantee uninterrupted service</li>
              <li>We may perform maintenance that temporarily affects availability</li>
              <li>Service availability may vary based on your subscription tier</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">9.2 Modifications</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We continuously improve our service and may add, modify, or remove features</li>
              <li>Material changes that negatively impact core functionality will be communicated in advance</li>
              <li>Continued use after changes constitutes acceptance of modifications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Data Processing and Storage</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">10.1 Data Retention</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>We retain your data for as long as your account is active</li>
              <li>You may request deletion of your data at any time</li>
              <li>Some data may be retained as required by law or for legitimate business purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">10.2 Data Portability</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You may export your data at any time through our interface</li>
              <li>We provide data in commonly used formats</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Limitation of Liability</h2>
            <div className="p-4 bg-gray-100 border-l-4 border-gray-500 text-gray-800">
              <p className="font-semibold mb-2">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>OUR SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE"</li>
                <li>WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED</li>
                <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES</li>
                <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU IN THE PAST 12 MONTHS</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Indemnification</h2>
            <p className="text-gray-700 mb-3">You agree to indemnify and hold harmless FlowDocs and its affiliates from any claims, damages, or expenses arising from:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Your use of the service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Termination</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">13.1 By You</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>You may terminate your account at any time</li>
              <li>Termination does not entitle you to any refunds</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">13.2 By Us</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>We may terminate or suspend your account for violation of these Terms</li>
              <li>We may terminate service with 30 days' notice for any reason</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">13.3 Effect of Termination</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Upon termination, your right to use the service ceases immediately</li>
              <li>We may delete your data after a reasonable retention period</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Governing Law and Disputes</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">14.1 Governing Law</h3>
            <p className="text-gray-700 mb-4">
              These Terms are governed by the laws of the State of Oregon, United States, without regard to conflict of law principles.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">14.2 Dispute Resolution</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We encourage resolution through good faith negotiations</li>
              <li>Any disputes shall be resolved through binding arbitration in Oregon</li>
              <li>You waive any right to a jury trial or class action</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Beta Features and Early Access</h2>
            <p className="text-gray-700">
              From time to time, we may offer access to features that are in development. Such features are provided "as is" 
              and may be discontinued or significantly changed at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Changes to Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. We will notify you of material changes via email or 
              through the service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Information</h2>
            <p className="text-gray-700 mb-3">For questions about these Terms, please contact us at:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Email: <a href="mailto:legal@getflowdocs.com" className="text-blue-600 hover:underline">legal@getflowdocs.com</a></li>
              <li>Website: <a href="https://getflowdocs.com" className="text-blue-600 hover:underline">getflowdocs.com</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">18. Miscellaneous</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>These Terms constitute the entire agreement between you and FlowDocs</li>
              <li>If any provision is found unenforceable, the remaining provisions continue in effect</li>
              <li>Our failure to enforce any right or provision is not a waiver</li>
              <li>You may not assign your rights under these Terms without our consent</li>
            </ul>
          </section>

        </article>
      </main>
      
      <Footer />
      <Chatbot />
    </div>
  );
}